<script setup lang="ts">
  import { ref, computed, nextTick, onMounted, useSlots, watch, defineComponent, h } from 'vue';
  // Here, we've to import both components because they're dynamically used/unused
  // based on a runtime decision (searchable prop)
  // see: https://vuetifyjs.com/en/features/treeshaking/#dynamic-components
  // Also, importing from 'vuetify/components/<group>' is better to avoid loading styles
  // that you are not using during development
  // see: https://vuetifyjs.com/en/features/treeshaking/#import-groups
  import { VAutocomplete } from 'vuetify/components/VAutocomplete';
  import { VChip } from 'vuetify/components/VChip';
  import { VDivider } from 'vuetify/components/VDivider';
  import { VSelect } from 'vuetify/components/VSelect';
  import { VSkeletonLoader } from 'vuetify/components/VSkeletonLoader';
  import { VTooltip } from 'vuetify/components/VTooltip';
  import { Intersect } from 'vuetify/directives/intersect';
  import { VCheckboxBtn } from 'vuetify/lib/components/VCheckbox/VCheckboxBtn';
  import { VListItem } from 'vuetify/lib/components/VList/VListItem';
  import { VListItemAction } from 'vuetify/lib/components/VList/VListItemAction';

  import { getErrorMessages } from '@bv/helpers/form';
  import { debounce } from '@bv/utils/common';
  import { toSystemColor } from '@bv/utils/common/color/to-system-color';

  import CloseIcon from './partials/CloseIcon.vue';
  import SelectMenuIcon from './partials/SelectMenuIcon.vue';

  import type { ErrorObject } from '@vuelidate/core';

  // Component options
  defineOptions({
    inheritAttrs: false,
    directives: {
      Intersect,
    },
  });

  export type Item = Record<string, any> | string | number;

  type Model = Item | Item[];

  export interface GetterConfig {
    params?: any;
    headers?: any;
  }

  export interface GetterItemsPaginated {
    data: Item[];
    meta: Meta;
  }

  // Props
  export interface Props {
    searchKeys?: (keyof Item)[] | keyof Item;
    items?: Item[];
    itemsGetter?: (config?: GetterConfig) => Promise<Item[] | GetterItemsPaginated>;
    syncSearchKey?: string;
    getterConfig?: GetterConfig;
    chipsLength?: number;
    showAllChip?: boolean;
    hideIcon?: boolean;
    multiple?: boolean;
    allowAll?: boolean;
    checkBoxes?: boolean;
    color?: string;
    bgColor?: string;
    label?: string;
    noData?: string;
    placeholder?: string;
    rules?: (string | boolean | ((value: any) => string | boolean))[];
    required?: boolean;
    itemTitle?: keyof Item | ((item: any) => string);
    itemValue?: keyof Item;
    returnObject?: boolean;
    error?: boolean;
    errorMessages?: string | string[] | ErrorObject | ErrorObject[];
    searchable?: boolean;
    clearable?: boolean;
    disabled?: boolean;
    loading?: string | boolean;
    size?: 'sm' | 'md';
  }

  const props = withDefaults(defineProps<Props>(), {
    items: undefined,
    itemsGetter: undefined,
    getterConfig: () => ({}),
    syncSearchKey: 'search',
    searchKeys: () => [],
    chipsLength: 5,
    showAllChip: false,
    hideIcon: false,
    multiple: false,
    allowAll: false,
    checkBoxes: false,
    color: 'base',
    bgColor: 'transparent',
    label: '',
    noData: '',
    placeholder: '',
    rules: () => [],
    required: false,
    itemTitle: 'name' as keyof Item,
    itemValue: 'id' as keyof Item,
    returnObject: true,
    error: false,
    errorMessages: undefined,
    searchable: true,
    clearable: true,
    disabled: false,
    loading: false,
    size: 'md',
  });

  // Model
  const model = defineModel<Model | null>({ default: null });

  const slots = useSlots();

  // Dynamic component
  const SelectComponent = computed(() => (props.searchable ? VAutocomplete : VSelect));

  // Vuetify reserves room for the floating label whenever the label slot is filled,
  // so it's only rendered when there is something to show.
  const hasLabel = computed<boolean>(() => !!props.label || !!slots.label);

  const colors = computed(() => ({
    color: toSystemColor(props.color),
    bgColor: toSystemColor(props.bgColor),
  }));

  // get localized error message(s) from props.errorMessages
  const translatedMessage = computed(() => {
    return getErrorMessages(props.errorMessages);
  });

  const hasError = computed<boolean>(() => props.error || translatedMessage.value.length > 0);

  // Select all
  const checkedAll = computed<boolean>(() => {
    return Array.isArray(model.value) && model.value.length === internalItems.value.length;
  });

  /**
   * Toggles the selection of all available items.
   * If all items are selected, it deselects all. Otherwise, it selects all.
   * Respects the `returnObject` prop to determine what to store in the model.
   * @returns {void}
   */
  const toggleAll = (): void => {
    if ((model.value as Item[])?.length === internalItems.value.length) {
      model.value = [];
    } else {
      model.value = props.returnObject
        ? internalItems.value
        : internalItems.value.map((item) => (typeof item !== 'object' ? item : item[props.itemValue]));
    }
  };

  // Items
  const internalItems = computed<Item[]>(() => {
    return props.items || apiItems.value;
  });

  const apiItems = ref<Item[]>([]);

  const isPaginated = ref<boolean>(false);

  /**
   * Asynchronously fetches items using the `itemsGetter` prop.
   * It handles both paginated and non-paginated responses and resets items on page 1.
   * @param {number} [page=1] - The page number to fetch.
   * @returns {Promise<void>}
   */
  const getNextItems = async (page: number = 1): Promise<void> => {
    if (props.itemsGetter) {
      loadingNextPage.value = true;
      if (page === 1) {
        apiItems.value = [];
      }

      const config = { ...props.getterConfig };
      config.params ??= {};
      config.params.page = page;
      config.params[props.syncSearchKey] = internalSearch.value || undefined;

      return props
        .itemsGetter(config)
        .then((response) => {
          if (Array.isArray(response)) {
            apiItems.value = response;
          } else {
            if (response.meta.current_page === 1) {
              apiItems.value = [];
            }
            apiItems.value.push(...response.data);
            isPaginated.value = !!response.meta;
            meta.value = response.meta;
          }
        })
        .finally(() => {
          loadingNextPage.value = false;
        });
    }
  };

  const filteredItems = computed<Item[]>(() => {
    if (!props.searchable) {
      return internalItems.value;
    }

    if (props.itemsGetter && isPaginated.value) {
      return internalItems.value;
    }

    const search = internalSearch.value.toString().toLowerCase();
    if (!search) {
      return internalItems.value;
    }

    return internalItems.value.filter((item) => {
      if (typeof item === 'string' || typeof item === 'number') {
        return `${item}`.toLowerCase().includes(search);
      } else if (searchKeys.value.length === 0) {
        const title: string = typeof props.itemTitle === 'function' ? props.itemTitle(item) : props.itemTitle;
        const value = item[title];
        return value?.toString().toLowerCase().includes(search) ?? false;
      } else {
        return !!searchKeys.value.some((key) => item[key].toString().toLowerCase().includes(search));
      }
    });
  });

  onMounted(() => {
    getNextItems();
  });

  // Infinity scroll
  interface Meta {
    current_page: number;
    from: number;
    last_page: number;
    per_page: number;
    to: number;
    total: number;
  }

  const meta = ref<Meta>({
    current_page: 1,
    from: 0,
    last_page: 1,
    per_page: 10,
    to: 0,
    total: 0,
  });

  const loadingNextPage = ref<boolean>(false);

  const LoadNextPageOnScrollToEnd = computed<boolean>(() => {
    return meta.value.current_page < meta.value.last_page;
  });

  /**
   * Triggered by an intersection observer at the end of the items list.
   * Loads the next page of items if there is one and a load is not already in progress.
   * @param {boolean} isIntersecting - Whether the trigger element is currently intersecting the viewport.
   * @returns {void}
   */
  const loadNextPage = (isIntersecting: boolean) => {
    if (isIntersecting && LoadNextPageOnScrollToEnd.value && !loadingNextPage.value) {
      getNextItems(meta.value.current_page + 1);
    }
  };

  // Search
  const internalSearch = ref<string>('');
  const valueToSearch = ref<string>('');
  const isMenuOpen = ref<boolean>(false);

  // Vuetify has no menu-icon slot: it feeds this prop to VIcon, which renders it
  // through VComponentIcon as `h(icon)` with no props at all. Wrap it so the icon
  // still receives the live loading/size/open values. The reference must stay
  // stable -- building the vnode inside the computed would give it a new component
  // type every render, remounting the icon and killing the chevron transition.
  const BoundMenuIcon = defineComponent({
    name: 'BvSelectBoundMenuIcon',
    setup() {
      return () =>
        h(SelectMenuIcon, {
          loading: !!(props.loading || loadingNextPage.value),
          size: props.size,
          isMenuOpen: isMenuOpen.value,
        });
    },
  });

  const menuIconValue = computed(() => (props.hideIcon ? '' : BoundMenuIcon));

  // Vuetify rewrites search on focus, blur, and select. Those writes must not
  // replace the query the user typed.
  const ignoreSearchSync = ref<boolean>(false);

  // Vuetify opens the menu when the list refills while the field is focused.
  const ignoreMenuOpen = ref<boolean>(false);

  const suppressSearchSync = (): void => {
    ignoreSearchSync.value = true;
    nextTick(() => {
      ignoreSearchSync.value = false;
    });
  };

  /**
   * Syncs the menu state, dropping opens that the component did not ask for.
   * @param {boolean} open - The menu state requested by the component.
   * @returns {void}
   */
  const onUpdateMenu = (open: boolean): void => {
    if (open && ignoreMenuOpen.value) {
      return;
    }
    isMenuOpen.value = open;
  };

  /**
   * Restores the last fetched query when the field regains focus, so the search
   * box always matches the loaded list.
   * @returns {void}
   */
  const onFocusIn = (): void => {
    suppressSearchSync();
    internalSearch.value = valueToSearch.value;
  };

  /**
   * Clears the search alongside the selection and reloads the unfiltered list.
   * @returns {void}
   */
  const onClickClear = (): void => {
    if (!internalSearch.value && !valueToSearch.value) {
      return;
    }

    internalSearch.value = '';
    valueToSearch.value = '';
    ignoreMenuOpen.value = !isMenuOpen.value;

    getNextItems().finally(() => {
      nextTick(() => {
        ignoreMenuOpen.value = false;
      });
    });
  };

  const searchKeys = computed<string[]>(() => {
    return typeof props.searchKeys === 'string' ? [props.searchKeys] : props.searchKeys;
  });

  /**
   * Updates the internal search reactive state.
   * Ignores Vuetify's programmatic clear/rewrite on focus, blur, and select.
   * @param {string | null} [search=null] - The new search term from the component.
   * @returns {void}
   */
  const onUpdateSearch = (search: string | null = null): void => {
    if (ignoreSearchSync.value) {
      return;
    }
    internalSearch.value = search || '';
  };

  const updateValueToSearchDebounce = ref(
    debounce(() => {
      // Only refetch while the user is searching with the menu open.
      // Selecting an item closes the menu and updates search to the title —
      // that must not clear/reload the list or reopen the dropdown.
      if (!isMenuOpen.value) {
        return;
      }
      if (valueToSearch.value !== internalSearch.value) {
        valueToSearch.value = internalSearch.value;
        getNextItems();
      }
    }),
  );

  watch(
    () => internalSearch.value,
    () => {
      updateValueToSearchDebounce.value();
    },
  );

  //  selected chips label +20 Others
  const otherLabel = computed(() => {
    return props.chipsLength === 0
      ? `${(model.value as Item[])?.length} Selected`
      : `+${(model.value as Item[])?.length - props.chipsLength} Other`;
  });

  const toolTipLabel = computed(() => {
    return (model.value as Item[])
      .map((item: Item) => (typeof item === 'object' ? item[props.itemTitle as keyof Item] : item))
      .join(', ');
  });
</script>

<template>
  <component
    :is="SelectComponent"
    v-model="model"
    autocomplete="off"
    :chips="multiple"
    class="bv-input bv-select"
    :class="[
      {
        'bv-input--sm': size === 'sm',
        'bv-input--md': size === 'md',
        'bv-input--disabled': disabled,
        'bv-input--error': hasError,
        'bv-input--focused': isMenuOpen,
        'bv-select--labeled': hasLabel,
        'bv-select--multiple': multiple,
      },
      $attrs.class,
    ]"
    :clearable
    closable-chips
    :color="colors.color"
    :disabled="disabled"
    :error
    :error-messages="translatedMessage"
    hide-details="auto"
    :item-title
    :item-value
    :items="filteredItems"
    :loading="loading || loadingNextPage"
    :menu="isMenuOpen"
    :menu-icon="menuIconValue"
    :menu-props="{ zIndex: 2500 }"
    :multiple
    :no-filter="isPaginated || !searchable"
    persistent-placeholder
    :placeholder
    :return-object
    :rules
    :search="internalSearch"
    variant="outlined"
    @click:clear="onClickClear"
    @focusin="onFocusIn"
    @focusout="suppressSearchSync"
    @update:menu="onUpdateMenu"
    @update:model-value="suppressSearchSync"
    @update:search="onUpdateSearch"
  >
    <template
      v-if="hasLabel"
      #label
    >
      <slot
        v-if="$slots.label"
        name="label"
      />
      <template v-else>
        {{ label }}
        <span
          v-if="required"
          class="text-red-500"
        >
          *
        </span>
      </template>
    </template>

    <template #no-data>
      <p
        v-if="!loadingNextPage"
        class="min-h-4 cursor-default p-3 text-center"
      >
        {{ noData || 'No items available' }}
      </p>
    </template>

    <template #chip="{ item, index, props }">
      <slot
        v-if="$slots.chip"
        :index="index"
        :item="item.raw"
        name="chip"
        :props="props"
      />
      <template v-else-if="multiple">
        <v-chip
          v-if="checkedAll && showAllChip && index === 0"
          v-bind="props"
          class="bv-select__chip"
          :closable="false"
          density="compact"
          text="All"
          variant="flat"
        />
        <template v-else-if="!checkedAll || !showAllChip">
          <v-chip
            v-if="chipsLength === -1 || index < chipsLength"
            v-bind="props"
            class="bv-select__chip"
            closable
            density="compact"
            :text="item.title"
            variant="flat"
            @click.stop
            @mousedown.stop
          >
            <template #close>
              <close-icon class="bv-select__chip-close" />
            </template>
          </v-chip>
          <v-tooltip
            v-else-if="index === chipsLength"
            location="bottom left"
            :text="toolTipLabel"
          >
            <template v-slot:activator="{ props }">
              <v-chip
                v-bind="props"
                class="bv-select__chip"
                :closable="false"
                density="compact"
                :text="otherLabel"
                variant="flat"
              />
            </template>
          </v-tooltip>
        </template>
      </template>
      <template v-else>
        {{ item.title }}
      </template>
    </template>

    <template
      v-if="internalItems.length > 0 && allowAll && multiple"
      #prepend-item
    >
      <v-list-item
        :active="checkedAll"
        class="px-3"
        :color="colors.color"
        title="All"
        @click="toggleAll"
      >
        <template
          v-if="checkBoxes"
          #prepend
        >
          <v-list-item-action start>
            <v-checkbox-btn
              class="[&_.v-selection-control\_\_input:has([aria-checked=mixed])]:text-base-500"
              :indeterminate="Array.isArray(model) && !!model?.length && !checkedAll"
              :model-value="checkedAll"
            />
          </v-list-item-action>
        </template>
      </v-list-item>
      <v-divider class="my-1" />
    </template>

    <template #item="{ props, item, index }">
      <v-list-item
        v-bind="props"
        class="relative px-3 [&_+.v-list-item]:before:absolute [&_+.v-list-item]:before:-inset-x-3 [&_+.v-list-item]:before:top-0 [&_+.v-list-item]:before:mx-5 [&_+.v-list-item]:before:h-0.25 [&_+.v-list-item]:before:bg-gray-100"
        :color="colors.color"
        title=""
      >
        <template
          v-if="checkBoxes"
          #prepend="{ isSelected, select }"
        >
          <v-list-item-action start>
            <v-checkbox-btn
              :model-value="isSelected"
              @update:model-value="select"
            />
          </v-list-item-action>
        </template>
        <template v-if="$slots.item">
          <slot
            :index="index"
            :item="item.raw"
            name="item"
          />
        </template>
        <template v-else>
          {{ item.title }}
        </template>
      </v-list-item>
    </template>

    <template
      v-if="LoadNextPageOnScrollToEnd || loadingNextPage"
      #append-item
    >
      <div v-intersect="loadNextPage">
        <v-skeleton-loader
          :key="loadingNextPage ? 1 : 0"
          class="next-page-loader rounded-0 bg-base-500/[.12] max-h-10 overflow-hidden"
          type="image"
        />
      </div>
    </template>
  </component>
</template>

<style lang="scss" scoped>
  @import '@bv/assets/styles/select';

  .bv-select {
    --bv-input-color: v-bind('colors.color');
    --bv-input-bg-color: v-bind('colors.bgColor');
  }
</style>
